import { AfterViewInit, Component, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SearchConfig } from '../../../modules/variant-page/modules/variant-page/models';
import { RootSearchStore, RootUtilityStore } from '../../../store';
import { QuestionnaireComponent } from '../../containers/questionnaire/questionnaire.component';
import { ClickCursorDirective } from '../../directives/click-cursor.directive';
import { I18nPipe } from '../../i18n/i18n.pipe';
import { RtlDirective } from '../../i18n/rtl.directive';
import { DotsLoaderComponent } from '../dots-loader/dots-loader.component';

@Component({
  selector: 'gnx-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  imports: [
    RtlDirective,
    ClickCursorDirective,
    DotsLoaderComponent,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    QuestionnaireComponent,
    I18nPipe,
  ],
})
export class SearchBarComponent implements OnInit, AfterViewInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);

  readonly questionnaireRef = viewChild<QuestionnaireComponent>('questionnaire');

  protected searchStore = inject(RootSearchStore);

  private destroyRef = inject(DestroyRef);
  searchControl: FormControl;
  searchContext: SearchConfig;
  private utilityStore = inject(RootUtilityStore);

  ngOnInit() {
    this.searchControl = this.fb.control('');

    const term = this.searchStore.term();
    if (term) {
      this.searchControl.setValue(term, { emitEvent: false });
    }

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.searchContext = this.searchContext?.zygosity === 'TUMOR' ? { zygosity: 'TUMOR' } : null;
        const currentValue = this.searchControl.value;
        this.searchControl.setValue('');
        this.searchControl.setValue(currentValue, { emitEvent: false });
        this.searchControl.markAsPristine();
      });
  }

  ngAfterViewInit() {
    this.questionnaireRef().setIsTumor(this.isTumor);
  }

  get hgValue(): string {
    return localStorage.getItem('lastHGValue') || 'hg19';
  }

  set hgValue(value: string) {
    localStorage.setItem('lastHGValue', value);
    if (this.searchControl.pristine) {
      this.onSearch();
    }
  }

  get isTumor(): boolean {
    return localStorage.getItem('isTumorSearch') === 'true';
  }

  set isTumor(value: boolean) {
    localStorage.setItem('isTumorSearch', `${value}`);
    this.questionnaireRef().setIsTumor(value);
    if (this.searchControl.pristine) {
      this.onSearch();
    }
  }

  onSearch(redirect = true) {
    if (this.searchControl.value) {
      setTimeout(() => {
        this.utilityStore.searchVariant(this.searchControl.value, redirect, this.hgValue, {
          ...this.searchContext,
          search_term: this.searchControl.value,
        });
      });
    } else {
      this.onEscape();
    }
  }

  onEscape() {
    this.searchControl.setValue('', { emitEvent: false });
    this.searchContext = this.searchContext?.zygosity === 'TUMOR' ? { zygosity: 'TUMOR' } : null;
    this.searchStore.clearSearchError();
  }

  onContextChange(context: SearchConfig) {
    this.searchContext = context;
  }

  onPaste() {
    setTimeout(() => {
      this.searchControl.markAsDirty();
      this.searchControl.updateValueAndValidity();
    });
  }
}
